import React from 'react';
import axios from 'axios';
import { useMutation } from 'react-query';
import { mdiConnection } from '@mdi/js';
import Grid, { GridItem } from '@/components/layout/Grid';
import VBox from '@/components/layout/VBox';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import AlertBox from '@/components/layout/AlertBox';
import Botao from '@/components/Botao';
import {
    AuthTipoMcp,
    McpServidor,
    TesteConexaoMcp,
    authTiposMcp,
} from '@tera/shared/src/types/McpServidor';

type Props = {
    value?: McpServidor | null;
    onChange?: (value: McpServidor) => void;
    fieldErrors?: Record<string, string>;
};

const FormMcpServidor = ({ value, onChange, fieldErrors }: Props) => {
    const [teste, setTeste] = React.useState<TesteConexaoMcp | null>(null);

    const testarMutation = useMutation(
        async () => {
            // O que está na tela vai no corpo, para o teste valer também antes
            // de salvar e logo depois de mexer na URL ou no token.
            const corpo = {
                nome: value?.nome,
                url: value?.url,
                auth_tipo: value?.auth_tipo,
                auth_header: value?.auth_header,
                auth_valor: value?.auth_valor,
                timeout_ms: value?.timeout_ms,
            };

            const res = await axios.post(
                value?.id ? `/api/mcp_servidor/${value.id}/testar` : '/api/mcp_servidor/testar',
                corpo,
            );
            return res.data.result as TesteConexaoMcp;
        },
        {
            onSuccess: setTeste,
            onError: (error: any) =>
                setTeste({
                    ok: false,
                    ferramentas: [],
                    erro: error.response?.data?.msg ?? 'Erro ao testar a conexão.',
                }),
        },
    );

    if (!value) return null;

    const alterar = (mudanca: Partial<McpServidor>) =>
        onChange?.({ ...value, ...mudanca } as McpServidor);

    // Basta a URL: o backend testa com o que veio da tela, sem gravar nada.
    const podeTestar = !!value.url?.trim();

    // Opções da allowlist: o que o teste descobriu, mais o que já está salvo —
    // senão uma ferramenta liberada some do seletor quando o servidor está fora
    // do ar, e salvar apagaria a liberação.
    const opcoesFerramentas = Array.from(
        new Set([
            ...(teste?.ferramentas.map((f) => f.nome) ?? []),
            ...(value.ferramentas_permitidas ?? []),
        ]),
    ).map((nome) => ({ id: nome, label: nome }));

    return (
        <Grid>
            <GridItem sm={12} md={6}>
                <CampoTexto
                    instanceId="mcp-nome"
                    label="Nome"
                    value={value.nome ?? ''}
                    onChange={(v: string) => alterar({ nome: v })}
                    error={fieldErrors?.nome}
                />
            </GridItem>
            <GridItem sm={12} md={6}>
                <CampoCheckbox
                    instanceId="mcp-ativo"
                    label="Ativo"
                    value={value.ativo !== false}
                    onChange={(v: boolean) => alterar({ ativo: v })}
                />
            </GridItem>

            <GridItem sm={12}>
                <CampoTexto
                    instanceId="mcp-url"
                    label="URL do servidor"
                    placeholder="https://exemplo.com/mcp"
                    value={value.url ?? ''}
                    onChange={(v: string) => alterar({ url: v })}
                    error={fieldErrors?.url}
                    tooltipText="Somente servidores MCP remotos por HTTP. Servidores locais (stdio, via npx) não são suportados."
                />
            </GridItem>

            <GridItem sm={12}>
                <CampoTexto
                    instanceId="mcp-descricao"
                    label="Descrição"
                    value={value.descricao ?? ''}
                    onChange={(v: string) => alterar({ descricao: v })}
                    multiline
                    error={fieldErrors?.descricao}
                    tooltipText="Vai para o prompt do agente, explicando para que serve este sistema."
                />
            </GridItem>

            <GridItem sm={12} md={4}>
                <CampoSeletor
                    instanceId="mcp-auth-tipo"
                    label="Autenticação"
                    value={value.auth_tipo ?? 'none'}
                    onChange={(v: string | null) =>
                        alterar({ auth_tipo: (v ?? 'none') as AuthTipoMcp })
                    }
                    options={authTiposMcp.map((t) => ({ id: t.id, label: t.label }))}
                    error={fieldErrors?.auth_tipo}
                />
            </GridItem>

            {value.auth_tipo === 'header' && (
                <GridItem sm={12} md={4}>
                    <CampoTexto
                        instanceId="mcp-auth-header"
                        label="Nome do header"
                        placeholder="X-API-Key"
                        value={value.auth_header ?? ''}
                        onChange={(v: string) => alterar({ auth_header: v })}
                        error={fieldErrors?.auth_header}
                    />
                </GridItem>
            )}

            {value.auth_tipo !== 'none' && (
                <GridItem sm={12} md={4}>
                    <CampoTexto
                        instanceId="mcp-auth-valor"
                        label={value.auth_tipo === 'bearer' ? 'Bearer token' : 'Valor do header'}
                        type="password"
                        // Campo vazio num cadastro que já tem token parece
                        // credencial perdida; o placeholder diz que ela está lá.
                        placeholder={value.tem_credencial ? '(Salvo)' : ''}
                        value={value.auth_valor ?? ''}
                        onChange={(v: string) => alterar({ auth_valor: v })}
                        error={fieldErrors?.auth_valor}
                        tooltipText={
                            value.tem_credencial
                                ? 'Já existe uma credencial gravada. Deixe em branco para mantê-la, ou digite outra para substituir.'
                                : undefined
                        }
                    />
                </GridItem>
            )}

            <GridItem sm={12} md={4}>
                <CampoTexto
                    instanceId="mcp-timeout"
                    label="Timeout (ms)"
                    type="number"
                    value={String(value.timeout_ms ?? 30000)}
                    onChange={(v: string) => alterar({ timeout_ms: Number(v) || 30000 })}
                    error={fieldErrors?.timeout_ms}
                />
            </GridItem>

            <GridItem sm={12}>
                <VBox gap={8}>
                    <Botao
                        variant="secondary"
                        icon={mdiConnection}
                        title="Testar conexão"
                        disabled={!podeTestar || testarMutation.isLoading}
                        onClick={() => testarMutation.mutate()}
                    >
                        {testarMutation.isLoading ? 'Conectando...' : 'Testar conexão'}
                    </Botao>

                    {!podeTestar && (
                        <p>Informe a URL do servidor para testar a conexão e listar as ferramentas.</p>
                    )}

                    {teste && !teste.ok && (
                        <AlertBox variant="danger">
                            <p>{teste.erro}</p>
                        </AlertBox>
                    )}

                    {teste?.ok && (
                        <AlertBox variant="success">
                            <p>
                                Conectado. O servidor anuncia {teste.ferramentas.length}{' '}
                                ferramenta(s).
                            </p>
                        </AlertBox>
                    )}
                </VBox>
            </GridItem>

            <GridItem sm={12}>
                <CampoMultiSeletor
                    instanceId="mcp-ferramentas-permitidas"
                    label="Ferramentas liberadas"
                    placeholder="(Todas)"
                    options={opcoesFerramentas}
                    value={value.ferramentas_permitidas ?? []}
                    onChange={(v: string[]) => alterar({ ferramentas_permitidas: v })}
                    tooltipText="Vazio libera todas as ferramentas que o servidor anunciar, inclusive as que ele passar a expor depois."
                />
                {!Boolean(teste?.ok) && (
                    <AlertBox variant="info">
                        Teste a conexão para ver todas as ferramentas disponíveis.
                    </AlertBox>
                )}
            </GridItem>

            <GridItem sm={12}>
                <AlertBox variant="warning">
                    <p>
                        O servidor MCP controla o texto que descreve suas ferramentas, e esse texto
                        entra no prompt do agente. Cadastre apenas servidores de origem confiável, e
                        prefira liberar só as ferramentas que o atendimento realmente usa.
                    </p>
                </AlertBox>
            </GridItem>
        </Grid>
    );
};

export default FormMcpServidor;
